import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/api";
import UnidadeSelector from "@/components/UnidadeSelector";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, Package, ArrowLeftRight, ClipboardList,
  Users, ShoppingBasket, HardHat, FileSpreadsheet,
  Building2, Trees, Network, UserCog, LogOut, BarChart3, Store, ShieldCheck, ScanLine, UtensilsCrossed, Undo2, Fuel, Truck, KeyRound, ShoppingCart, ShieldAlert, DollarSign,
} from "lucide-react";

const LOGO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_motoserra-depot/artifacts/18uda418_logo%20Padr%C3%A3o.png";

// roles allowed (admin always allowed)
const NAV = [
  { to: "/", label: "Painel", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/compras", label: "Painel Compras", icon: ShoppingCart, roles: ["compras", "presidencia"], testid: "nav-compras" },
  { to: "/compras/precos", label: "Ajuste de Preços", icon: DollarSign, roles: ["compras"], testid: "nav-compras-precos" },
  { to: "/ordens-pagamento", label: "Ordens de Pagamento", icon: DollarSign, roles: ["admin", "compras", "tesoureiro", "presidencia"], testid: "nav-payment-orders" },
  { to: "/bipar", label: "Bipar", icon: ScanLine, testid: "nav-scan" },
  { to: "/produtos", label: "Estoque", icon: Package, testid: "nav-products" },
  { to: "/movimentacoes", label: "Movimentações", icon: ArrowLeftRight, roles: ["almoxarife", "compras"], testid: "nav-movements" },
  { to: "/epis", label: "Entrega EPI", icon: HardHat, roles: ["almoxarife"], testid: "nav-epis" },
  { to: "/tickets", label: "Tickets Refeição", icon: UtensilsCrossed, roles: ["almoxarife"], testid: "nav-tickets" },
  { to: "/compras-comunidade", label: "Compras Comunitárias", icon: ShoppingBasket, roles: ["almoxarife", "compras"], testid: "nav-purchases" },
  { to: "/compras-fornecedor", label: "Compras Fornecedor", icon: Store, roles: ["almoxarife", "compras"], testid: "nav-sup-purchases" },
  { to: "/requisicoes", label: "Requisições", icon: ClipboardList, testid: "nav-reqs" },
  { to: "/combustivel", label: "Combustível", icon: Fuel, testid: "nav-fuel" },
  { to: "/saidas-combustivel", label: "Saída Combustível", icon: Fuel, roles: ["almoxarife", "tesoureiro", "presidencia"], testid: "nav-fuel-outs" },
  { to: "/devolucoes", label: "Devoluções", icon: Undo2, roles: ["almoxarife", "presidencia"], testid: "nav-returns" },
  { divider: "Cadastros" },
  { to: "/cooperados", label: "Cooperados", icon: Users, roles: ["almoxarife", "presidencia", "compras"], testid: "nav-cooperados" },
  { to: "/fornecedores", label: "Fornecedores", icon: Building2, roles: ["almoxarife", "presidencia", "compras"], testid: "nav-suppliers" },
  { to: "/comunidades", label: "Comunidades", icon: Trees, roles: ["almoxarife", "presidencia", "compras"], testid: "nav-communities" },
  { to: "/colaboradores", label: "Colaboradores", icon: UserCog, roles: ["almoxarife"], testid: "nav-collabs" },
  { to: "/veiculos", label: "Veículos", icon: Truck, roles: ["almoxarife", "tesoureiro", "presidencia"], testid: "nav-vehicles" },
  { to: "/setores", label: "Setores", icon: Network, roles: ["almoxarife"], testid: "nav-sectors" },
  { divider: "Análise" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, testid: "nav-reports" },
  { to: "/auditoria", label: "Auditoria", icon: ShieldAlert, roles: ["presidencia"], testid: "nav-audit" },
  { to: "/meu-pin", label: "Meu PIN", icon: KeyRound, roles: ["admin", "tesoureiro", "presidencia"], testid: "nav-pin" },
  { to: "/usuarios", label: "Usuários", icon: ShieldCheck, roles: [], testid: "nav-users" }, // admin only
];

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();

  const visibleNav = NAV.filter((n) => {
    if (n.divider) return true;
    if (!n.roles) return true; // public to all authenticated
    return hasRole(...n.roles);
  });

  const current = visibleNav.find((n) => n.to && (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))) || visibleNav[0];

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-[#3D6B57] text-white flex flex-col sticky top-0 h-screen overflow-y-auto print:hidden" data-testid="sidebar">
        <div className="px-4 py-4 border-b border-[#2D5A45] flex items-center gap-3">
          <img src={LOGO_URL} alt="COOMFLONA" className="w-14 h-14 object-contain" />
          <div className="min-w-0">
            <div className="font-display font-black text-sm uppercase tracking-tight leading-none">COOMFLONA</div>
            <div className="font-mono-data text-[9px] uppercase text-[#C8E0D2] mt-1 tracking-widest">Almoxarifado</div>
          </div>
        </div>

        <nav className="flex-1 py-3">
          {visibleNav.map((n, i) => {
            if (n.divider) {
              return (
                <div key={`d${i}`} className="px-5 mt-3 mb-1 font-mono-data text-[9px] uppercase tracking-widest text-[#A8D4B8]">
                  {n.divider}
                </div>
              );
            }
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={n.testid}
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#2D5A45] px-4 py-3">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#C8E0D2] mb-1">Usuário</div>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-[11px] text-[#C8E0D2] mb-2">{ROLE_LABEL[user?.role] || user?.role}</div>
          <button onClick={logout} className="brutal-btn bg-[#FF4F00] text-white w-full px-3 py-1.5 font-display font-bold uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2" data-testid="logout-btn">
            <LogOut className="w-3 h-3" /> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="bg-white border-b-2 border-[#111] px-8 py-4 flex items-center justify-between sticky top-0 z-20 print:hidden">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#777]">Módulo Ativo</div>
            <h1 className="font-display font-black text-xl uppercase tracking-tight leading-tight" data-testid="page-title">
              {current?.label || "Painel"}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <UnidadeSelector />
            <div className="hidden md:flex items-center gap-3 font-mono-data text-xs text-[#555]">
              <div className="w-2 h-2 bg-[#1A7F37] rounded-full" />
              <span>SISTEMA ONLINE</span>
              <span className="text-[#aaa]">|</span>
              <span>{new Date().toLocaleDateString("pt-BR")}</span>
            </div>
          </div>
        </header>
        <div className="p-6 lg:p-8 grid-bg min-h-[calc(100vh-73px)] print:p-0 print:grid-bg-none">
          <Outlet />
        </div>
        <footer className="border-t-2 border-[#111] bg-[#0A1F14] text-white/70 py-3 px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-2 print:hidden" data-testid="app-footer">
          <div className="font-mono-data text-[10px] uppercase tracking-widest">
            COOMFLONA · CNPJ 07.315.165/0001-00 · Santarém — PA
          </div>
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#FF9F5A]" data-testid="footer-credit">
            Desenvolvido por Railan Farias <span className="opacity-70">(Cooperado)</span>
          </div>
        </footer>
      </main>
    </div>
  );
}
